/* eslint-disable no-underscore-dangle */
import $ from 'jquery';
import _ from 'underscore';
import { $window, isNative } from 'util/global';
import {
  EVT_DAILYPROBLEM_MAINBODY_EXPANDED,
  EVT_QUIZ_POSITION_WILL_CHANGE,
  EVT_QUIZ_POSITION_DID_CHANGE,
} from 'util/constants';

/**
 * Map of Wistia video ids (hashes) to rendered video objects
 * @type {object}
 */
const existingVideos = {};

/**
 * Wrapper for the Wistia JS API since the global variable isn't very descriptive
 * @param {Object} data - Wistia API data object
 * @see {@link https://wistia.com/support/developers/player-api} for API docs
 */
function wistiaAPI(hash, callback, forceSimpleApi = false) {
  // window._wq and window.Wistia come from the Wistia JS
  // see: https://fast.wistia.com/assets/external/E-v1.js
  if (window.Wistia && !forceSimpleApi) {
    const video = window.Wistia.api(hash);
    if (video) {
      callback(video);
    } else {
      wistiaAPI(hash, callback, true);
    }
  } else {
    window._wq = window._wq || [];
    window._wq.push({
      id: hash,
      onHasData(video) {
        callback(video);
      },
    });
  }
}

/**
 * If the same video is used in multiple quiz problems/panes, Wistia melts down. To prevent this,
 * we're keeping an object of videos we've already rendered, and we're swapping in the prerendered
 * video (because we can't rely on Wistia to re-render it).
 * @param {string} videoId
 * @param {jquery} $container
 */
function setupQuizEdgeCases(videoId, $container) {
  const $template = $container.find('template');
  const $existingVideo = existingVideos[videoId];

  if ($existingVideo) {
    // destroyBeforeNav wasn't removing embed-wrapper, so removing them here
    $container.find('.embed-wrapper').remove();
    $container.append($existingVideo.find('.embed-wrapper').clone());
  } else if ($template.length) {
    const $video = $('<div>').html($template.html());
    $container.append($video.find('.embed-wrapper').clone());
    existingVideos[videoId] = $video;
  }
}

/**
 * Controller for videos in content
 * @param {JQuery} $container
 */
export function wistiaController($container) {
  const videoId = $container.data('video');

  setupQuizEdgeCases(videoId, $container);

  wistiaAPI(videoId, video => {
    const updateHeight = () => {
      if ($container.is(':visible')) {
        // In quizzes, we're sometimes reusing the same video in multiple problems/panes.
        // However, we can't assume that the video will always be the same size. Therefore,
        // we have to check the target video size and resize the container and video object
        // to match.
        let targetWidth = $container.data('width');
        let height = video.height();
        const props = {};
        if (!targetWidth) {
          targetWidth = $container.width();
        }
        // Prevent width > 100%
        const width = Math.min(targetWidth, $container.width());
        height = width / video.aspect();
        props.width = width;
        // This timeout prevents a race condition where we constantly recalculate the
        // video size
        setTimeout(() => video.height(height, { constrain: true }), 1);

        props.height = height;
        $container.children().css(props);
      }
    };

    updateHeight();

    video.bind('heightchange', updateHeight);
  });
}

/**
 * Handles all of the wistia-like functionality for our HTML5 video player (Bristia?)
 * @param {JQuery} $container - div container around the video tag
 */
export function videoController($container) {
  const videoId = $container.data('video');
  const assetList = $container.data('assets');
  const captionUrl = $container.data('caption-url');
  const $video = $container.find('video');
  const $track = $video.find('track');
  const width = +$video.attr('width');
  const height = +$video.attr('height');
  const HIDE = 'hide';
  const WIDTH = 'width';
  const PAUSED = 'paused';
  const STARTED = 'started';
  const PLAY = 'play';
  const videoToggleClass = 'video-toggle';

  // let's not autoplay videos that aren't muted and aren't visible (logged out)
  if (
    $video[0].hasAttribute('autoplay') &&
    !$video[0].hasAttribute('muted') &&
    !$container.is(':visible') &&
    !isNative
  ) {
    $video[0].removeAttribute('autoplay');
  }

  // if not in offline mode, check device resolution and set correct video source
  if (assetList !== undefined && !$video[0].src) {
    let videoWidth = $container.width();
    // find first non-zero parent and use it if it's smaller than video width
    $container.parents().each((index, el) => {
      const $parent = $(el);
      const parentWidth = $parent.width();
      if (parentWidth !== 0 && parentWidth <= window.innerWidth) {
        if (parentWidth < videoWidth || videoWidth === 0) {
          videoWidth = parentWidth;
        }
        return false; // break
      }
      return true;
    });
    // if the defined width is smaller than the container, use it
    if ($container.data(WIDTH) < videoWidth) {
      videoWidth = $container.data(WIDTH);
    }
    // const containerWidth = videoWidth;
    videoWidth *= window.devicePixelRatio;
    let deviceAsset = assetList.filter(asset => asset.width >= videoWidth);
    if (deviceAsset.length === 0) {
      deviceAsset = assetList[assetList.length - 1];
    } else {
      [deviceAsset] = deviceAsset;
    }
    $video[0].src = deviceAsset.url;
  }

  // have to add track after video for firefox
  if (captionUrl && $track.length > 0) {
    $track[0].src = captionUrl;
  }

  // add a play button
  if ($container.find(`button.${videoToggleClass}`).length === 0) {
    let buttonClasses = `${videoToggleClass} ${$video[0].paused ? PAUSED : ''}`;
    if (!$video[0].hasAttribute('muted')) {
      buttonClasses += ' has-controls';
    }
    $container.append(`<button aria-label="play" type="button" class="${buttonClasses}"></button>`);
  }
  $container.find(`.${videoToggleClass}`).on('click', e => {
    e.preventDefault();
    if ($video[0].paused === true) {
      $video[0].play();
    } else {
      $video[0].pause();
    }
  });

  // show play button when video is paused
  $video.on('play pause', e => {
    const $button = $container.find(`.${videoToggleClass}`);
    if (e.type === PLAY && !$button.hasClass(STARTED)) {
      $button.addClass(STARTED);
    }
    if ($video[0].paused === true) {
      $button.removeClass(PLAY);
      $button.addClass(PAUSED);
    } else {
      $button.removeClass(PAUSED);
      $button.addClass(PLAY);
    }
  });

  // when switching panes pause old video and check if we should unpause new video
  const pauseEventGroup = `${EVT_QUIZ_POSITION_WILL_CHANGE}.video${videoId}`;
  const unpauseEventGroup = `${EVT_QUIZ_POSITION_DID_CHANGE}.video${videoId}`;
  $window.on(pauseEventGroup, () => {
    if (document.body.contains($container[0])) {
      if ($video[0].paused === false) {
        $video[0].setAttribute('data-was-playing', true);
      } else {
        $video[0].removeAttribute('data-was-playing');
      }
      $video[0].pause();
      $window.off(pauseEventGroup);
      $window.on(unpauseEventGroup, () => {
        if (document.body.contains($container[0])) {
          const videoData = $video.data();
          if (videoData.wasPlaying || videoData.couldSetHeight === false) {
            $video[0].play();
          }
          $window.off(unpauseEventGroup);
        }
      });
    }
  });
  setTimeout(() => $video.removeClass(HIDE), 1);

  const checkHeight = () => {
    const actualWidth = $video.width();
    let actualHeight = height;
    if (actualWidth !== width) {
      actualHeight = Math.round((actualWidth / width) * height);
    }
    if (actualHeight) {
      $video.attr('height', actualHeight);
      $video.attr('data-could-set-height', true);
      $container[0].style.minHeight = `${actualHeight}px`;
    } else {
      $video.attr('data-could-set-height', false);
    }
  };

  $window.on(`resize ${EVT_DAILYPROBLEM_MAINBODY_EXPANDED} `, _.throttle(checkHeight, 16));
  _.defer(checkHeight);
}

/**
 * Replaces the wistia popover player
 * @param {JQuery} $button - button to open players
 */
export function popoverController($button) {
  const $modal = $button.find('.popover-video.modal');
  const $videoContainer = $modal.find('.video-container');
  $button.on('click', () => {
    $modal.modal();
    videoController($videoContainer);
  });
}

/**
 * Handle video on the homepage
 * @param {JQuery} $container
 */
export function videoPlaceholder($container) {
  const $button = $container.find('button');
  $button.on('click', () => {
    const $videoTemplate = $container.find('template');
    if ($videoTemplate.hasClass('wistia-video')) {
      $container.css('height', $container.height());
      wistiaAPI($container.data('video'), video => {
        // make sure tracking is ready
        setTimeout(() => video.play());
        $container.css('height', 'auto');
      });

      $container.html($container.find('template').html());
      $container.find('[data-controller*="app/videos:wistiaController"]').each((i, elem) => {
        wistiaController($(elem));
      });
    } else if ($videoTemplate.hasClass('video-container')) {
      $container.html($container.find('template').html());
      $container.find('[data-controller*="app/videos:videoController"]').each((i, elem) => {
        videoController($(elem));
        const $video = $container.find('video');
        $video[0].play();
      });
    }
  });
}
